import {
  ArrowRight,
  Check,
  Database,
  HeartHandshake,
  Lightbulb,
  ListChecks,
  Search,
} from "lucide-react";

import { MarketingContainer } from "./ui/MarketingContainer";

const traditionalItems = [
  {
    icon: Database,
    text: "Stores attendance and member records",
  },
  {
    icon: Search,
    text: "Requires leaders to search through reports",
  },
  {
    icon: ListChecks,
    text: "Relies on manual follow-up processes",
  },
];

const ministryIQItems = [
  {
    icon: HeartHandshake,
    text: "Identifies members who may need care",
  },
  {
    icon: Lightbulb,
    text: "Surfaces actionable ministry insights",
  },
  {
    icon: Check,
    text: "Helps leaders prioritize follow-up",
  },
];

export function WhyMinistryIQSection() {
  return (
    <section className="border-y border-white/5 bg-slate-900/40 py-24">
      <MarketingContainer>
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-300">
            Why MinistryIQ
          </p>

          <h2 className="mt-4 text-4xl font-black tracking-tight text-white sm:text-5xl">
            Traditional church software manages information.
            <span className="mt-2 block text-indigo-300">
              MinistryIQ helps pastors care for people.
            </span>
          </h2>

          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-400">
            Most church systems tell you what happened.
            MinistryIQ helps you understand what needs attention next.
          </p>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-white/10 bg-slate-950/70 p-8">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              Traditional Church Software
            </p>

            <h3 className="mt-4 text-2xl font-bold text-white">
              Data without direction
            </h3>

            <div className="mt-8 space-y-5">
              {traditionalItems.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.text}
                    className="flex items-start gap-4"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-slate-500">
                      <Icon className="h-5 w-5" />
                    </div>

                    <p className="pt-2 text-slate-400">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-indigo-400/25 bg-indigo-500/10 p-8">
            <div className="absolute right-0 top-0 h-48 w-48 rounded-full bg-indigo-500/20 blur-3xl" />

            <p className="relative text-sm font-semibold uppercase tracking-[0.18em] text-indigo-300">
              MinistryIQ
            </p>

            <h3 className="relative mt-4 text-2xl font-bold text-white">
              Insight that leads to action
            </h3>

            <div className="relative mt-8 space-y-5">
              {ministryIQItems.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.text}
                    className="flex items-start gap-4"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-400/15 text-indigo-200">
                      <Icon className="h-5 w-5" />
                    </div>

                    <p className="pt-2 text-slate-200">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="relative mt-8 inline-flex items-center gap-2 text-sm font-semibold text-indigo-200">
              Know who needs care next
              <ArrowRight className="h-4 w-4" />
            </div>
          </div>
        </div>

        <div className="mx-auto mt-20 max-w-4xl text-center">
          <p className="text-3xl font-bold leading-tight text-white sm:text-4xl">
            Ministry is not about collecting attendance.
            <span className="mt-2 block text-indigo-300">
              It is about caring for people.
            </span>
          </p>

          <p className="mt-5 text-lg text-slate-400">
            MinistryIQ helps you do both.
          </p>
        </div>
      </MarketingContainer>
    </section>
  );
}
